"use client"

import * as React from 'react'
import Link from 'next/link'
import type { InjectionWidgetComponentProps } from '@open-mercato/shared/modules/widgets/injection'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { Button } from '@open-mercato/ui/primitives/button'

type OrderRecord = {
  id?: string
  kind?: string
}

type ShipmentRow = {
  id: string
}

type CargoRow = {
  id: string
  cargoType: string
  referenceNumber: string
  currentMode?: string | null
  currentLocation?: {
    label?: string | null
    latitude?: number | null
    longitude?: number | null
  } | null
  assignments?: Array<{ salesShipmentId?: string | null }>
}

const formatLocation = (location: CargoRow['currentLocation']) => {
  if (!location) return null
  if (location.label) return location.label
  if (location.latitude !== null && location.latitude !== undefined && location.longitude !== null && location.longitude !== undefined) {
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
  }
  return null
}

export default function OrderCargoWidget({ data, context }: InjectionWidgetComponentProps<unknown, OrderRecord>) {
  const t = useT()
  const record = (data as OrderRecord | undefined) ?? undefined
  const orderId =
    (typeof record?.id === 'string' && record.id) ||
    (typeof (context as Record<string, unknown> | undefined)?.recordId === 'string'
      ? ((context as Record<string, unknown>).recordId as string)
      : null)
  const documentKind = record?.kind === 'quote' ? 'quote' : 'order'

  const [items, setItems] = React.useState<CargoRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let mounted = true

    const load = async () => {
      if (!orderId) {
        setItems([])
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const shipmentsResponse = await apiCall<{ items?: ShipmentRow[] }>(
          `/api/sales/shipments?orderId=${encodeURIComponent(orderId)}&pageSize=100`,
        )
        if (!shipmentsResponse.ok) {
          throw new Error(t('cargo.widgets.orderCargo.loadError', 'Failed to load cargo links.'))
        }

        const shipmentIds = (shipmentsResponse.result?.items ?? [])
          .map((entry) => entry.id)
          .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)

        if (!shipmentIds.length) {
          if (mounted) setItems([])
          return
        }

        const cargoResponse = await apiCall<{ items?: CargoRow[] }>(
          `/api/freight/cargo?salesShipmentIds=${encodeURIComponent(shipmentIds.join(','))}&pageSize=100`,
        )
        if (!cargoResponse.ok) {
          throw new Error(t('cargo.widgets.orderCargo.loadError', 'Failed to load cargo links.'))
        }

        if (!mounted) return
        setItems(Array.isArray(cargoResponse.result?.items) ? cargoResponse.result.items : [])
      } catch (loadError) {
        if (!mounted) return
        setError(loadError instanceof Error ? loadError.message : t('cargo.widgets.orderCargo.loadError', 'Failed to load cargo links.'))
      } finally {
        if (mounted) setLoading(false)
      }
    }

    void load()
    return () => {
      mounted = false
    }
  }, [orderId, t])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium">{t('cargo.widgets.orderCargo.title', 'Cargo')}</h3>
          <p className="text-sm text-muted-foreground">
            {documentKind === 'quote'
              ? t('cargo.widgets.orderCargo.quoteDescription', 'Cargo preview for shipments that will be linked after this offer becomes an order.')
              : t('cargo.widgets.orderCargo.description', 'Cargo linked to shipments created for this order.')}
          </p>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link href="/backend/cargo/create">
            {t('cargo.pages.create.action', 'Register cargo')}
          </Link>
        </Button>
      </div>

      {loading ? <LoadingMessage label={t('cargo.widgets.orderCargo.loading', 'Loading cargo…')} /> : null}
      {error ? <ErrorMessage label={error} /> : null}

      {!loading && !error && items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          {documentKind === 'quote'
            ? t('cargo.widgets.orderCargo.quoteEmpty', 'No cargo preview is available for this offer yet.')
            : t('cargo.widgets.orderCargo.empty', 'No cargo is linked to this order yet.')}
        </div>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <Link
              key={item.id}
              href={`/backend/cargo/${item.id}`}
              className="block rounded-lg border p-4 transition-colors hover:border-foreground/30"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{item.referenceNumber}</div>
                  <div className="text-sm text-muted-foreground">
                    {t(`cargo.types.${item.cargoType}`, item.cargoType)}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {item.currentMode ? t(`cargo.modes.${item.currentMode}`, item.currentMode) : t('cargo.state.modeUnknown', 'Mode unknown')}
                </div>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {formatLocation(item.currentLocation) ?? t('cargo.state.locationUnknown', 'Location unknown')}
              </div>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}
